import { BehaviorSubject, Observable, throwError } from 'rxjs';

import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { catchError, filter, switchMap, take } from 'rxjs/operators';

import { AuthenticationService } from "./authentication.service";
import { Helper } from 'src/app/helpers/helper.service';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { CookieManagerService } from './cookie.service';

interface TokenResponse {
    token: string;
}

@Injectable()
export class RequestInterceptorService implements HttpInterceptor {

    private isRefreshingToken: boolean = false;
    private tokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
    private readonly unauthorizedEndpoints: { method: string, url: string }[] = [
        { method: "POST", url: "users/login" },
        { method: "POST", url: "token/refresh" },
        { method: "POST", url: "users/reset-password" },
        { method: "POST", url: "users/forgot-password" },
        { method: "POST", url: "users/change-password" },
        { method: "POST", url: "users/term" },
    ];

    constructor(
        private authService: AuthenticationService, 
        private helper: Helper, 
        private router: Router,
        private translate: TranslateService,
        private cookieManager: CookieManagerService,
    ) {}

    /**
     * Checks if request is for an unauthorized endpoint 
    */
    private isUnauthorizedEndpoint(method: string, url: string): boolean {
        return this.unauthorizedEndpoints.some(endpoint => 
            endpoint.method === method && url.includes(endpoint.url)
        );
    }
    
    private shouldAppendToken(request: HttpRequest<any>): boolean {
        if (this.isUnauthorizedEndpoint(request.method, request.url)) return false;
        return true;
    }

    private addToken(req: HttpRequest<any>, token: string | null): HttpRequest<any> {
        const isFormData = typeof FormData !== 'undefined' && req.body instanceof FormData;
        const defaultHeaders = isFormData ? {} : { 'Content-Type': 'application/json' };

        if (this.shouldAppendToken(req)) {
            return req.clone({ setHeaders: {
                ...defaultHeaders,
                'Authorization': this.authService.getBearerHeader(token)
            }});
        }

        return req.clone({ setHeaders: defaultHeaders });
    }

    /**
     * Intercepts outgoing HTTP requests and handles 401 errors. 
     * 
     * Logs out the user in case refresh token is expired.
     * Checks for 401 errors and attempts to refresh the auth token if needed before retrying the request.
     * Handles 401 errors during token refresh to avoid looping.
     */
    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(this.addToken(req, this.authService.getToken())).pipe(
            catchError((error: HttpErrorResponse) => {
                if (req.url.endsWith('/token/refresh') && error.status === 401) {
                    this.isRefreshingToken = false;
                    this.cookieManager.deleteAllCookies();
                    this.router.navigateByUrl('/login', { replaceUrl: true });
                    this.helper.popMessageDialog(
                        this.translate.instant("auth.session.expired"), 
                        this.translate.instant("auth.refresh_token.login"), 
                        'error', 
                        false,
                    );
                    return throwError(error);
                }

                if (error.status === 401) {
                    return this.handle401Error(req, next);
                } else {
                    return throwError(error);
                }
            }),
        );
    }

    /**
     * Handles 401 unauthorized errors by attempting to refresh the auth token before retrying the request.
     * 
     * Checks if a token refresh is already in progress, and if not, sets the refresh flag and resets the token.
     * Calls the auth service to refresh the token, and on success, clones the request with the new token and retries it. 
     * Catches any errors during the token refresh and propagates them.
     * 
     * If a refresh is already in progress, it waits on the token subject for the new token, then retries the request.
    */
    private handle401Error(req: HttpRequest<any>, next: HttpHandler) {
        if (!this.isRefreshingToken) {
            this.isRefreshingToken = true;

            this.tokenSubject.next(null);
            
            return this.authService.refreshToken().pipe(
                switchMap((token: TokenResponse) => {
                    this.isRefreshingToken = false;
                    this.tokenSubject.next(token.token);
                    return next.handle(req.clone({
                        setHeaders: {
                            'Authorization': this.authService.getBearerHeader(token.token)
                        }
                    }));
                }),
                catchError((error) => {
                    this.isRefreshingToken = false;
                    return throwError(error);
                })
            );
        } else {
            return this.tokenSubject.pipe(
                filter(token => token != null),
                take(1),
                switchMap(token => next.handle(this.addToken(req, token))),
            );
        }
    }
}
